/**
 * Servicio para gestión de clientes
 *
 * Endpoints:
 * - GET /clientes?nombre={query} - Búsqueda de clientes (case-insensitive)
 * - GET /clientes/:codigo - Obtiene detalle completo con todas las direcciones
 *
 * Ver documentación completa: ../api/docs/api/clientes.md
 */

import axios from "axios";
import HostService from "./host.service";
import AuthService from "./auth.service";

/**
 * Servicio para gestión de clientes
 */
class ClientesService {
    /**
     * GET /clientes - Busca clientes por nombre
     *
     * @param {string} query - Texto de búsqueda (mínimo 2 caracteres)
     * @returns {Promise<Array>} Lista de clientes que coinciden con la búsqueda
     *
     * Retorna array de clientes con estructura:
     * {
     *   codigo: string,        // Código del cliente
     *   nombre: string,        // Nombre principal
     *   cif: string,          // CIF/NIF
     *   direccion: string,    // Dirección fiscal
     *   poblacion: string,    // Población
     *   provincia: string,    // Provincia
     *   direcciones: Array    // Array de direcciones de envío
     * }
     */
    static async search(query) {
        if (!query || query.trim().length < 2) {
            return [];
        }

        return axios
            .get(HostService.getHost() + 'clientes', {
                params: {
                    nombre: query.trim(),
                    limit: 20,
                },
                ...AuthService.setToken(),
            })
            .then((res) => {
                // La API retorna { data: { clientes: [...], pagination: {...} } }
                return res.data.data.clientes || [];
            })
            .catch((error) => {
                console.error('Error al buscar clientes:', error);
                throw new Error(error.response?.data?.error?.message || 'Error al buscar clientes');
            });
    }

    /**
     * GET /clientes/:codigo - Obtiene el detalle completo de un cliente
     *
     * @param {string} codigo - Código del cliente
     * @returns {Promise<Object>} Cliente con todas sus direcciones y teléfonos
     */
    static async getById(codigo) {
        return axios
            .get(HostService.getHost() + `clientes/${codigo}`, AuthService.setToken())
            .then((res) => {
                // La API retorna { data: {...cliente} }
                return res.data.data;
            })
            .catch((error) => {
                console.error('Error al obtener cliente:', error);
                throw new Error(error.response?.data?.error?.message || 'Error al obtener cliente');
            });
    }

    /**
     * Obtiene las direcciones de envío de un cliente
     *
     * @param {string} codigo - Código del cliente
     * @returns {Promise<Array>} Lista de direcciones formateadas
     *
     * Retorna array con estructura:
     * {
     *   linea: number,               // Número de línea (ID único dentro del cliente)
     *   nombre: string,              // Nombre de la dirección
     *   direccion: string,           // Dirección completa
     *   codigoPostal: string,        // Código postal
     *   poblacion: string,           // Población
     *   provincia: string,           // Provincia
     *   telefono: string,            // Teléfono
     *   direccionCompleta: string    // Dirección formateada para mostrar
     * }
     */
    static async getDirecciones(codigo) {
        try {
            const cliente = await this.getById(codigo);

            // Formatear direcciones para el selector
            return (cliente.direcciones || []).map(dir => ({
                ...dir,
                direccionCompleta: this.formatearDireccion(dir),
            }));
        } catch (error) {
            console.error('Error al obtener direcciones:', error);
            throw error;
        }
    }

    /**
     * Formatea una dirección para mostrarla en el selector
     *
     * @param {Object} direccion - Objeto con datos de la dirección
     * @returns {string} Dirección formateada
     */
    static formatearDireccion(direccion) {
        const partes = [];

        if (direccion.nombre) {
            partes.push(direccion.nombre);
        }

        if (direccion.direccion) {
            partes.push(direccion.direccion);
        }

        const ubicacion = [
            direccion.codigoPostal,
            direccion.poblacion,
            direccion.provincia,
        ].filter(Boolean).join(' ');

        if (ubicacion) {
            partes.push(ubicacion);
        }

        return partes.join(' - ');
    }
}

export default ClientesService;
